import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PharmacyItemsTable } from '../common';
import { PharmacyItem as FormPharmacyItem, PrescriptionItem } from '../newPharmacyForm.helper';
import { formatDateDMY } from '../../../../../utils/dateUtils';

interface PreviousPrescribedProps {
  prescriptions: PrescriptionItem[];
  onSelectPrescription?: (prescription: PrescriptionItem) => void;
  onMoveItems?: (items: FormPharmacyItem[]) => void;
}

/**
 * Helper to convert PrescriptionItem to FormPharmacyItem
 */
const mapPrescriptionToFormItem = (prescription: PrescriptionItem): FormPharmacyItem => {
  // Map available batches to the form format
  const availableBatches = (prescription.available_batches || []).map((batch) => ({
    batchNumber: batch.batch_number,
    expiryDate: formatDateDMY(batch.expiry_date),
    isExpired: batch.is_expired,
    baseMrp: batch.base_mrp,
    baseCostPrice: batch.base_cost_price,
  }));

  return {
    prescriptionId: prescription.prescription_id,
    itemId: prescription.item_id,
    visitDate: formatDateDMY(prescription.visit_date),
    itemCode: prescription.item_code,
    itemName: prescription.item_name,
    batchCode: '', // Don't auto-patch batch code
    expiryDate: '', // Don't auto-patch expiry date
    manufacturer: prescription.manufacturer || '',
    availableBatches: availableBatches,
    prescribedStrength: prescription.prescribed_strength || prescription.strength || '',
    prescribedQty: prescription.prescribed_qty,
    prescribedUOM: prescription.prescribed_uom,
    billedStrength: prescription.strength || '',
    billedQty: 0, // Don't auto-patch billed qty
    billedUOM: '', // Don't auto-patch billed UOM
    frequency: '1-0-1',
    rack: '',
    shelf: '',
    container: '',
    checked: false,
    baseMRP: 0,
    baseCostPrice: 0,
    totalMRPAmount: 0,
    concessionAmount: 0,
    concessionPercent: 0,
    sgstAmount: 0,
    sgstPercent: 0,
    cgstAmount: 0,
    cgstPercent: 0,
    netAmount: 0,
    pregnancyClass: '',
    provider: prescription.prescribed_by || '',
    uomConversions: prescription.uom_conversions || prescription.uom_object || [],
  };
};

/**
 * PreviousPrescribed - Displays previous prescription items in a collapsible section
 * Uses the common PharmacyItemsTable without billing columns and delete action
 */
const PreviousPrescribed: React.FC<PreviousPrescribedProps> = ({
  prescriptions,
  onMoveItems,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Convert prescriptions to FormPharmacyItem format
  const initialItems = useMemo(() => 
    prescriptions?.map(mapPrescriptionToFormItem) || [], 
  [prescriptions]
  );
  
  const [items, setItems] = useState<FormPharmacyItem[]>(initialItems);

  // Update items when prescriptions change
  React.useEffect(() => {
    setItems(prescriptions?.map(mapPrescriptionToFormItem) || []);
  }, [prescriptions]);

  if (!prescriptions || prescriptions.length === 0 || items.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSelectChange = (index: number) => {
    const updated = [...items];
    const item = updated[index];
    const newChecked = !item.checked;
    updated[index] = { ...item, checked: newChecked };
    
    // If checking the item, move it to main items list
    if (newChecked && onMoveItems) {
      onMoveItems([updated[index]]);
      // Remove the item from prescribed list
      updated.splice(index, 1);
    }
    
    setItems(updated);
  };

  const handleFieldChange = (index: number, field: keyof FormPharmacyItem, value: string | number) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header with expand/collapse - light green background */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
          px: 2,
          mb: 0,
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: '#E8F5E9',
          borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
        }}
        onClick={handleToggle}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Previous Prescribed ({items.length})
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <PharmacyItemsTable
          items={items}
          onSelectChange={handleSelectChange}
          onFieldChange={handleFieldChange}
          showBillingColumns={true}
          showDeleteAction={false}
          readOnly={false}
          showHeader={true}
        />
      </Collapse>
    </Box>
  );
};

export default PreviousPrescribed;
